/**
 * reply-gate-copy — what the operator is told when the reply gate declines to draft.
 *
 * Deliberately its own module with no Supabase import, so the branch order below can be unit-tested
 * without standing up a client. The gate's server half lives in api/_lib/reply-gate.ts.
 */

/** Shape of one entry in the regenerate endpoint's results array, narrowed to what the copy needs. */
export interface RegenerateResultLike {
  success: boolean;
  blocked?: boolean;
  rule?: string;
  newMethodology?: string;
  constraintScore?: number;
  /** Defer only: false means the follow-up date never reached the database. */
  followUpScheduled?: boolean;
}

export interface ToastCopy {
  title: string;
  description: string;
  variant?: 'destructive';
}

export const REPLY_GATE_OVERRIDE_ACTION_LABEL = 'Draft anyway';
export const REPLY_GATE_OVERRIDE_ACTION_ALT_TEXT =
  'Overrule the reply gate and create a draft for approval without sending it';

const OVERRIDE_GUIDANCE =
  'Choose “Draft anyway” to overrule this decision. Tacit will create a draft for approval and will not send it.';

/**
 * Copy for a reply-gate closing decision, shared by every surface that can trigger generation.
 *
 * A refusal must never be silent. Before this existed, Inbox showed nothing at all when the gate
 * declined — its handler had only an onError branch — so the operator clicked "Generate" and watched
 * the row quietly vanish.
 */
export const describeGateBlock = (rule?: string, followUpScheduled?: boolean): ToastCopy => {
  switch (rule) {
    case 'emoji_only':
      return {
        title: 'Closed — emoji only',
        description: 'They replied with an emoji, not a message. Tacit closed the conversation instead of drafting a reply.',
      };
    case 'unsubscribe_request':
      return {
        title: 'Closed — they asked to be removed',
        description: 'This reply asks us to stop contacting them. Tacit closed the conversation and wrote nothing.',
      };
    case 'explicit_refusal':
      return {
        title: 'Closed — they declined',
        description: 'This reply is an explicit no. Tacit closed the conversation instead of drafting a reply.',
      };
    case 'temporal_refusal':
      // The false branch is the honest one, and it is the reason this parameter exists. The
      // sentence below promises the operator a return date. If the write that stores that date
      // failed, the conversation is still resolved and still out of the Inbox — but nothing will
      // ever bring it back, and saying otherwise turns a bug into a lie the operator acts on.
      if (followUpScheduled === false) {
        return {
          title: 'Deferred — follow-up NOT saved',
          description: 'They declined for now, and Tacit closed this draft — but the follow-up date could not be stored, so this conversation will not come back on its own. Reopen it, or generate again to retry.',
          variant: 'destructive',
        };
      }
      return {
        title: 'Deferred — not right now',
        description: 'They declined for now. Tacit closed this draft and set a follow-up 45 days after their reply; it will appear under Follow-up in the Inbox when it comes due. Nothing was scheduled or sent.',
      };
    default:
      return {
        title: 'Closed by the reply gate',
        description: 'Tacit judged this reply not worth answering and closed the conversation.',
      };
  }
};

/** Inbox-only copy for a gate block that is rendered alongside the explicit override control. */
export const describeGateBlockWithOverride = (rule?: string, followUpScheduled?: boolean): ToastCopy => {
  const copy = describeGateBlock(rule, followUpScheduled);
  return { ...copy, description: `${copy.description} ${OVERRIDE_GUIDANCE}` };
};

/** Copy after an explicit override; approval remains a separate, required operator action. */
export const describeGateOverrideSuccess = (): ToastCopy => ({
  title: 'Draft created for review',
  description: 'Reply gate overruled. Review and approve the draft before anything is sent.',
});

/**
 * describeRegenerateResult — what the operator should be told after a regenerate.
 *
 * The BLOCKED branch must come first. A blocked result carries `success: true`, because refusing is
 * the endpoint working — so a handler that checks `success` first reports "Draft regenerated — New
 * approach: AUTO" for a draft that was never written. That was the live behaviour in inbox.tsx
 * before this function existed, and reply-gate-copy.test.ts pins the ordering.
 */
export const describeRegenerateResult = (result: RegenerateResultLike | undefined): ToastCopy => {
  if (result?.blocked) return describeGateBlock(result.rule, result.followUpScheduled);
  if (result?.success) {
    return {
      title: 'Draft regenerated',
      description: `New approach: ${result.newMethodology?.toUpperCase() || 'auto'} (constraints: ${result.constraintScore || 0}/7)`,
    };
  }
  return {
    title: 'Regeneration failed',
    description: 'Tacit could not regenerate this draft. Try again when you are ready.',
    variant: 'destructive',
  };
};
